import { useState, useCallback } from 'react';
import { useSnackbar } from '../contexts/SnackbarProvider';
import { SnackbarType } from '../components/Snackbar';
import { getErrorMessage } from '../utils/formatters';

interface ApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

type ApiCallback<T, P> = (params: P) => Promise<T>;

/**
 * API呼び出し用のカスタムフック
 * エラーハンドリングとローディング状態を共通化します
 */
export const useApi = <T, P = void>(
  apiCallback: ApiCallback<T, P>, 
  options: {
    onSuccess?: (data: T) => void;
    onError?: (error: unknown) => void;
    successMessage?: string;
    errorMessage?: string;
  } = {}
) => {
  const showSnackbar = useSnackbar();
  const [state, setState] = useState<ApiState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const execute = useCallback(async (params: P) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      
      const result = await apiCallback(params);
      
      setState(prev => ({
        ...prev,
        data: result,
        loading: false,
      }));
      
      if (options.successMessage) {
        showSnackbar(options.successMessage, SnackbarType.SUCCESS);
      }
      
      if (options.onSuccess) {
        options.onSuccess(result);
      }
      
      return result;
    } catch (error) {
      const errorMessage = options.errorMessage || getErrorMessage(error);
      
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
      
      showSnackbar(errorMessage, SnackbarType.ERROR);
      
      if (options.onError) {
        options.onError(error);
      }
      
      return null;
    }
  }, [apiCallback, options, showSnackbar]);

  return {
    ...state,
    execute,
    reset: useCallback(() => {
      setState({
        data: null,
        loading: false,
        error: null,
      });
    }, []),
  };
}; 